import React, {useState} from 'react';

import sessionStore from 'client/Session/SessionStore';
import useBreakpoint from 'lib/hooks/useBreakpoint';
import {local} from 'lib/StorageUtil';

import {GetStartedModule} from './Home/GetStartedModule';
import {Home} from './Home/Home';
import {TeacherDashboardLayout} from './Layout/TeacherDashboardLayout';

interface Props {
  location?: {
    pathname: string;
  };
}

/**
 * Teacher Home owns the Get Started rail. On wide screens it's a persistent side panel whose collapsed
 * state is remembered (localStorage). On narrow screens (< m) it's an overlay that defaults closed — so
 * it never covers the content on load and re-closes whenever we drop to narrow
 */
export const TeacherDashboard = ({location}: Props) => {
  const pathname = location?.pathname ?? '/home';
  const storageKey = `${sessionStore.getUserId()}-homeHideGetStarted`;
  const [collapsed, setCollapsed] = useState<boolean>(() => Boolean(local.get(storageKey)));
  const [narrowRailOpen, setNarrowRailOpen] = useState(false);

  // Below the `m` breakpoint (1020px) the rail can't sit beside the content, so it becomes an overlay.
  // matchMedia flips only when the breakpoint is actually crossed — no per-pixel resize re-render.
  const {isMAndUp} = useBreakpoint();
  const isNarrow = !isMAndUp;

  const updateCollapsed = (next: boolean) => {
    local.set(storageKey, next);
    setCollapsed(next);
  };

  // `collapsed` is the saved open/closed preference; `narrowRailOpen` only keeps the overlay from
  // auto-covering content on a fresh narrow load. Both sync on every toggle, so an intentionally closed
  // rail stays closed (and an open one stays open) when the viewport crosses the breakpoint.
  const railOpen = isNarrow ? narrowRailOpen : !collapsed;
  const openRail = () => {
    updateCollapsed(false);
    setNarrowRailOpen(true);
  };
  const closeRail = () => {
    updateCollapsed(true);
    setNarrowRailOpen(false);
  };

  return (
    <TeacherDashboardLayout
      pathname={pathname}
      rightRail={<GetStartedModule onDismiss={closeRail} open={railOpen} overlay={isNarrow} />}
    >
      <Home getStartedCollapsed={!railOpen} onReopenGetStarted={openRail} />
    </TeacherDashboardLayout>
  );
};
